import React, {
    ButtonHTMLAttributes,
    KeyboardEvent,
    MouseEvent,
    memo,
    useCallback,
    useMemo,
} from "react";
import { Link, type LinkProps, useLocation, matchPath } from "react-router-dom";
import { HudButtonContainer } from "./HudButton.styles";
import type {
    HudNavItem,
    NonClickableHudNavItem,
} from "widgets/hud/model/routes";
import { useHudOverlay } from "widgets/hud/model/overlayStore";
import {useHudCollapse} from "widgets/hud/model/collapseStore.ts";

type BaseProps = {
    item: HudNavItem;
    isMobileMenu?: boolean;
    noCollapse?: boolean;
    ref?: React.RefObject<any | null>
} & Pick<React.HTMLAttributes<HTMLElement>, "aria-label" | "aria-current" | "role" | "title">;

export type HudButtonProps =
    & BaseProps
    & Partial<LinkProps>
    & Partial<ButtonHTMLAttributes<HTMLButtonElement>>;

const isLinkItem = (item: HudNavItem): item is NonClickableHudNavItem =>
    !("isClickable" in item) || item.isClickable === false;

const computeActiveByPath = (item: NonClickableHudNavItem, pathname: string): boolean => {
    if (item.match === "exact") {
        if (item.to.includes(":")) return Boolean(matchPath({ path: item.to, end: true }, pathname));
        return pathname === item.to;
    }
    const base = item.to.split("/:")[0];
    return pathname.startsWith(base);
};

const RawHudButton = (props: HudButtonProps) => {
    const { item, onClick, onKeyDown, noCollapse, isMobileMenu, ...rest } = props;
    const location = useLocation();

    let collapsed = useHudCollapse((s) => s.collapsed);

    if (noCollapse) {
        collapsed = false;
    }

    const openId = useHudOverlay(s => s.openId);
    const toggle = useHudOverlay(s => s.toggle);
    const closeOverlay = useHudOverlay(s => s.close);

    // Активность:
    //  - если открыт любой оверлей — активна только кнопка этого оверлея;
    //  - если оверлеев нет — активность ссылок по URL, кнопки не активны.
    const active = useMemo(() => {
        if (openId) return !isLinkItem(item) && openId === item.id;
        return isLinkItem(item) ? computeActiveByPath(item, location.pathname) : false;
    }, [item, location.pathname, openId]);

    const IconComp = active ? item.iconActive : item.icon;
    const iconFill = active ? (item as any).iconActiveFill : (item as any).iconFill;

    // ----- ССЫЛКА -----
    if (isLinkItem(item)) {
        const linkRest = rest as Partial<LinkProps>;
        return (
            <HudButtonContainer
                ref={props.ref}
                as={Link}
                to={item.to}
                $isMobileMenu={isMobileMenu}
                $active={active}
                $collapsed={collapsed}
                onClick={(e: any) => {
                    (linkRest as any)?.onClick?.(e);
                    closeOverlay();
                }}
                {...linkRest}
            >
                <IconComp fill={iconFill} />
                {!collapsed && item.label}
            </HudButtonContainer>
        );
    }

    // ----- КНОПКА -----
    const btnRest = rest as Partial<ButtonHTMLAttributes<HTMLButtonElement>>;

    // eslint-disable-next-line
    const handleClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onClick?.(e);
        if (!e.defaultPrevented) {
            // повторный клик по той же кнопке закрывает, по другой — переключает
            toggle(item.id);
        }
    }, [onClick, toggle, item.id]);

    // eslint-disable-next-line
    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLButtonElement>) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle(item.id);
        }
    }, [onKeyDown, toggle, item.id]);


    return (
        <HudButtonContainer
            ref={props.ref}
            $active={active}
            $isMobileMenu={isMobileMenu}
            $collapsed={collapsed}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            {...btnRest}
        >
            <IconComp fill={iconFill} />
            {!collapsed && item.label}
        </HudButtonContainer>
    );
};

export const HudButton = memo(RawHudButton);
